import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { Editor } from "react-draft-wysiwyg";
import { EditorState, convertToRaw, ContentState } from "draft-js";
import draftToHtml from "draftjs-to-html";
import DOMPurify from "dompurify";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import Wizard from "./Wizard"; // Same wizard as in CompanyAssessment
import styles from "./EditDepartment.module.css";

const CompanyEditDepartment = () => {
  // 1) Route & navigate
  const { admincode } = useParams();
  const navigate = useNavigate();

  // 2) Department data states
  const [companyName, setCompanyName] = useState("");
  const [departmentName, setDepartmentName] = useState(""); // from master list
  const [companyDepartmentId, setCompanyDepartmentId] = useState("");

  // 3) Fields from sb_getlandingpage / sb_editcompanydepartment
  const [admin_site_url, setAdminSiteUrl] = useState("");
  const [imageFile, setImageFile] = useState("");
  const [adminemails, setAdminemails] = useState("");
  const [adminpageheader, setAdminpageheader] = useState("");
  const [adminpagecontent, setAdminpagecontent] = useState("");
  const [adminpageoffers, setAdminpageoffers] = useState("");
  const [adminpagecta, setAdminpagecta] = useState("");
  const [assessmentRecipient, setAssessmentRecipient] = useState("");

  // 4) Offers & recipients as arrays
  const [recipientsArray, setRecipientsArray] = useState([]);
  const [offersArray, setOffersArray] = useState([]);
  const [recipientInput, setRecipientInput] = useState("");
  const [offerInput, setOfferInput] = useState("");

  // 5) Editor
  const [editorState, setEditorState] = useState(
    EditorState.createWithContent(ContentState.createFromText(""))
  );

  // 6) UI states
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [formErrors, setFormErrors] = useState({});

  // 7) Master assessment types
  const [masterTypes, setMasterTypes] = useState([]);

  // ───────────────────────────────────────────────────────────
  //  A) Fetch Master Types
  // ───────────────────────────────────────────────────────────
  const fetchMasterAssessmentType = async () => {
    try {
      const resp = await axios.post(
        "https://casaahaanahotels.com/get_master_assessment_type",
        {}
      );
      if (Array.isArray(resp.data)) {
        setMasterTypes(resp.data);
      }
    } catch (err) {
      console.error("Error fetching master assessment types:", err);
    }
  };

  // ───────────────────────────────────────────────────────────
  //  B) Fetch Department Data
  // ───────────────────────────────────────────────────────────
  const fetchDepartmentData = async () => {
    setLoading(true);
    setErrorMessage("");
    setSuccessMessage("");
    setFormErrors({});

    try {
      const res = await axios.post(
        "https://casaahaanahotels.com/sb_getlandingpage",
        {
          usercode: admincode,
        }
      );
      if (res.data && res.data.user_data && res.data.user_data.length > 0) {
        const data = res.data.user_data[0];

        // Basic fields
        setCompanyName(data.companyname || "");
        setCompanyDepartmentId(data.company_department || "");
        setAdminSiteUrl(data.admin_site_url || "");
        setImageFile(data.imageFile || "");
        setAdminemails(data.adminemails || "");
        setAdminpageheader(data.adminpageheader || "");
        setAdminpageoffers(data.adminpageoffers || "");
        setAdminpagecta(data.adminpagecta || "");
        setAssessmentRecipient(data.assessmentRecipient || "");

        // Editor Content
        if (data.adminpagecontent) {
          setAdminpagecontent(data.adminpagecontent);
          // For a simpler approach, parse HTML to text and set as editor content
          const plainText = data.adminpagecontent.replace(/<[^>]+>/g, "");
          const contentState = ContentState.createFromText(plainText);
          setEditorState(EditorState.createWithContent(contentState));
        }

        // Recipients
        if (data.assessmentRecipient) {
          const recArr = data.assessmentRecipient
            .split(",")
            .map((r) => r.trim())
            .filter(Boolean);
          setRecipientsArray(recArr);
        }

        // Offers
        if (data && data.adminpageoffers) {
          if (Array.isArray(data.adminpageoffers)) {
            // Already an array
            setOffersArray(data.adminpageoffers);
          } else if (typeof data.adminpageoffers === "string") {
            // Fallback if API sends string
            const offArr = data.adminpageoffers
              .split(",")
              .map((o) => o.trim())
              .filter(Boolean);
            setOffersArray(offArr);
          }
        }
      } else {
        setErrorMessage("No department data found for this admincode.");
      }
    } catch (error) {
      console.error("Error fetching department data:", error);
      setErrorMessage("Failed to load department data.");
    }
    setLoading(false);
  };

  // ───────────────────────────────────────────────────────────
  //  C) useEffect to load data
  // ───────────────────────────────────────────────────────────
  useEffect(() => {
    fetchMasterAssessmentType();
    fetchDepartmentData();
  }, [admincode]);

  // D) Match the department name from master list
  useEffect(() => {
    if (companyDepartmentId && masterTypes.length > 0) {
      const found = masterTypes.find(
        (item) => item.assessment_type_id === companyDepartmentId
      );
      if (found) {
        setDepartmentName(found.AssessmentType);
      } else {
        setDepartmentName("Unknown Department");
      }
    }
  }, [companyDepartmentId, masterTypes]);

  // ───────────────────────────────────────────────────────────
  //  E) Validation
  // ───────────────────────────────────────────────────────────
  const isValidEmail = (email) => {
    const regex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
    return regex.test(email);
  };

  const isValidUrl = (url) => {
    const urlRegex = /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/\S*)?$/;
    return urlRegex.test(url);
  };

  const validateForm = () => {
    let errors = {};

    // Recipients
    if (recipientsArray.length === 0) {
      errors.assessmentRecipient = "At least one recipient is required.";
    } else {
      for (let i = 0; i < recipientsArray.length; i++) {
        if (!isValidEmail(recipientsArray[i])) {
          errors.assessmentRecipient = `Invalid email format: ${recipientsArray[i]}`;
          break;
        }
      }
    }

    // URL
    if (!admin_site_url.trim()) {
      errors.admin_site_url = "Site URL is required.";
    } else if (!isValidUrl(admin_site_url.trim())) {
      errors.admin_site_url = "Invalid URL format.";
    }

    // Page Header
    if (!adminpageheader.trim()) {
      errors.adminpageheader = "Page header is required.";
    }

    // Page Content
    if (!adminpagecontent.trim()) {
      errors.adminpagecontent = "Page content is required.";
    }

    // Offers
    if (offersArray.length === 0) {
      errors.adminpageoffers = "At least one offer is required.";
    }

    // CTA
    if (!adminpagecta.trim()) {
      errors.adminpagecta = "Call to Action is required.";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // ───────────────────────────────────────────────────────────
  //  F) Handle recipients/offers
  // ───────────────────────────────────────────────────────────
  const addRecipient = () => {
    if (!recipientInput.trim()) {
      alert("Please enter an email address first.");
      return;
    }
    setRecipientsArray([...recipientsArray, recipientInput.trim()]);
    setRecipientInput("");
  };

  const removeRecipient = (index) => {
    const updated = [...recipientsArray];
    updated.splice(index, 1);
    setRecipientsArray(updated);
  };

  const addOffer = () => {
    if (!offerInput.trim()) {
      alert("Please enter an offer first.");
      return;
    }
    setOffersArray([...offersArray, offerInput.trim()]);
    setOfferInput("");
  };

  const removeOffer = (index) => {
    const updated = [...offersArray];
    updated.splice(index, 1);
    setOffersArray(updated);
  };

  // ───────────────────────────────────────────────────────────
  //  G) Draft.js Editor
  // ───────────────────────────────────────────────────────────
  const handleEditorStateChange = (newState) => {
    setEditorState(newState);
    const rawContent = convertToRaw(newState.getCurrentContent());
    const htmlContent = draftToHtml(rawContent);
    const cleanedHtml = DOMPurify.sanitize(htmlContent);
    setAdminpagecontent(cleanedHtml);
  };

    const handleEditorChange = (content) => {
    const cleanedHtml = DOMPurify.sanitize(content);
    setAdminpagecontent(cleanedHtml); // directly update your state
  };

  // ───────────────────────────────────────────────────────────
  //  H) File upload
  // ───────────────────────────────────────────────────────────
  const handleFileChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    if (file.size > 1024 * 1024) {
      setFormErrors((prev) => ({
        ...prev,
        imageFile: "Image size should be less than 1MB.",
      }));
      return;
    }
    setFormErrors((prev) => ({ ...prev, imageFile: "" }));

    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await axios.post(
        "https://starbehaviors.com/uploadimage.php",
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      // Suppose the server returns { fileName: "someUnique.jpg" }
      if (response.data && response.data.fileName) {
        setImageFile(response.data.fileName);
      } else {
        throw new Error("Invalid response during file upload.");
      }
    } catch (err) {
      console.error("Image upload failed:", err);
      setFormErrors((prev) => ({
        ...prev,
        imageFile: "Image upload failed.",
      }));
    }
  };

  // ───────────────────────────────────────────────────────────
  //  I) Clear form
  // ───────────────────────────────────────────────────────────
  const clearForm = () => {
    setAdminSiteUrl("");
    setImageFile("");
    setAdminemails("");
    setAdminpageheader("");
    setAdminpagecontent("");
    setAdminpageoffers("");
    setAdminpagecta("");
    setAssessmentRecipient("");
    setRecipientsArray([]);
    setOffersArray([]);
    setRecipientInput("");
    setOfferInput("");
    setEditorState(
      EditorState.createWithContent(ContentState.createFromText(""))
    );
    setFormErrors({});
    setErrorMessage("");
    setSuccessMessage("");
  };

  // ───────────────────────────────────────────────────────────
  //  J) Save changes -> sb_editcompanydepartment
  // ───────────────────────────────────────────────────────────
  const handleSave = async () => {
    setErrorMessage("");
    setSuccessMessage("");

    if (!validateForm()) {
      return;
    }

    const recipientsString = recipientsArray.join(",");
    const offersString = offersArray.join(",");

    setLoading(true);

    const dataToSend = {
      admincode,
      admin_site_url,
      imageFile: imageFile || "",
      adminemails,
      adminpageheader,
      adminpagecontent,
      adminpageoffers: offersString,
      adminpagecta,
      assessmentRecipient: recipientsString,
    };

    try {
      const response = await axios.post(
        "https://casaahaanahotels.com/sb_editcompanydepartment",
        dataToSend,
        { headers: { "Content-Type": "application/json" } }
      );

      if (response.data && response.data.Message) {
        const msg = response.data.Message;
        if (msg.toLowerCase().includes("success")) {
          setSuccessMessage(msg);
          // After 2 seconds, navigate back to manage department
          setTimeout(() => {
            navigate("/company");
          }, 2500);
        } else {
          setErrorMessage(msg);
        }
      } else {
        setErrorMessage("Failed to update department. Unknown error.");
      }
    } catch (error) {
      console.error("Error updating department:", error);
      if (
        error.response &&
        error.response.data &&
        error.response.data.Message
      ) {
        setErrorMessage(error.response.data.Message);
      } else {
        setErrorMessage("An error occurred while updating the department.");
      }
    }
    setLoading(false);
  };

  // ───────────────────────────────────────────────────────────
  //  K) Render
  // ───────────────────────────────────────────────────────────
  return (
    <div className={styles.formContainer}>
      <h1 className={styles.companyPageTitle}>Edit Department</h1>

      {/* Show Company Name below title */}
      {companyName && (
        <div
          style={{
            textAlign: "center",
            fontSize: "1.1rem",
            marginBottom: "1rem",
          }}
        >
          Company: <strong>{companyName}</strong>
        </div>
      )}

      {loading && (
        <div className={styles.errorMessage}>Loading department data...</div>
      )}

      <hr className={styles.sectionDivider} />
      <h2>Edit Department Fields</h2>

      <form id="editDepartmentForm" className={styles.assignForm}>
        {/* Department Name (read-only) */}
        <div className={styles.formRow}>
          <label>Department Name</label>
          <input
            className={styles.selectField}
            value={departmentName}
            readOnly
          />
        </div>

        {/* Admin Site URL */}
        <div className={styles.formRow}>
          <label>Admin Site URL*</label>
          <input
            className={styles.selectField}
            placeholder="https://admin.example.com"
            value={admin_site_url}
            onChange={(e) => setAdminSiteUrl(e.target.value)}
          />
          {formErrors.admin_site_url && (
            <div className={styles.errorMessage}>
              {formErrors.admin_site_url}
            </div>
          )}
        </div>

        {/* Page Header */}
        <div className={styles.formRow}>
          <label>Page Header*</label>
          <input
            className={styles.selectField}
            placeholder="Landing Page Header"
            value={adminpageheader}
            onChange={(e) => setAdminpageheader(e.target.value)}
          />
          {formErrors.adminpageheader && (
            <div className={styles.errorMessage}>
              {formErrors.adminpageheader}
            </div>
          )}
        </div>

        {/* Call to Action */}
        <div className={styles.formRow}>
          <label>Call to Action*</label>
          <input
            className={styles.selectField}
            placeholder="Join Now"
            value={adminpagecta}
            onChange={(e) => setAdminpagecta(e.target.value)}
          />
          {formErrors.adminpagecta && (
            <div className={styles.errorMessage}>{formErrors.adminpagecta}</div>
          )}
        </div>

        {/* Upload Image */}
        <div className={styles.formRow}>
          <label>Upload Image (optional)</label>
          <input
            type="file"
            onChange={handleFileChange}
            className={
              formErrors.imageFile ? styles.errorInput : styles.selectField
            }
          />
          {formErrors.imageFile && (
            <div className={styles.errorMessage}>{formErrors.imageFile}</div>
          )}
          {imageFile && (
            <div style={{ fontSize: "0.9rem", marginTop: "4px" }}>
              Current Image: <strong>{imageFile}</strong>
            </div>
          )}
        </div>

        {/* Admin Emails */}
        <div className={styles.formRow}>
          <label>Admin Emails (optional)</label>
          <input
            className={styles.selectField}
            placeholder="admin@example.com"
            value={adminemails}
            onChange={(e) => setAdminemails(e.target.value)}
          />
        </div>

        {/* Assessment Recipients */}
        <div className={styles.formRow}>
          <label>Assessment Recipient(s)*</label>
          <div style={{ display: "flex", gap: "8px", width: "100%" }}>
            <input
              className={styles.selectField}
              placeholder="email@example.com"
              value={recipientInput}
              onChange={(e) => setRecipientInput(e.target.value)}
            />
            <button
              type="button"
              className={styles.submitButton}
              style={{ width: "120px" }}
              onClick={addRecipient}
            >
              Add
            </button>
          </div>
          {formErrors.assessmentRecipient && (
            <div className={styles.errorMessage}>
              {formErrors.assessmentRecipient}
            </div>
          )}
        </div>
        {recipientsArray.length > 0 && (
          <div className={styles.formRow}>
            <label>Recipients:</label>
            <ul style={{ listStyle: "none", paddingLeft: 0 }}>
              {recipientsArray.map((r, i) => (
                <li
                  key={i}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: "4px",
                  }}
                >
                  <span>{r}</span>
                  <button
                    type="button"
                    className={styles.submitButton}
                    style={{ backgroundColor: "#dc3545", width: "30px" }}
                    onClick={() => removeRecipient(i)}
                  >
                    X
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Landing Page Offers */}
        <div className={styles.formRow}>
          <label>Landing Page Offers*</label>
          <div style={{ display: "flex", gap: "8px", width: "100%" }}>
            <input
              className={styles.selectField}
              placeholder="Add an offer..."
              value={offerInput}
              onChange={(e) => setOfferInput(e.target.value)}
            />
            <button
              type="button"
              className={styles.submitButton}
              style={{ width: "120px" }}
              onClick={addOffer}
            >
              Add
            </button>
          </div>
          {formErrors.adminpageoffers && (
            <div className={styles.errorMessage}>
              {formErrors.adminpageoffers}
            </div>
          )}
        </div>
        {offersArray.length > 0 && (
          <div className={styles.formRow}>
            <label>Offers:</label>
            <ul style={{ listStyle: "none", paddingLeft: 0 }}>
              {offersArray.map((offer, idx) => (
                <li
                  key={idx}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: "4px",
                  }}
                >
                  <span>{offer}</span>
                  <button
                    type="button"
                    className={styles.submitButton}
                    style={{ backgroundColor: "#dc3545", width: "30px" }}
                    onClick={() => removeOffer(idx)}
                  >
                    X
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Editor - Full width */}
        <div className={styles.editorRow}>
          <label>Landing Page Main Content*</label>
          <div className={styles.editorWrapper}>
            {/* <Editor
              editorState={editorState}
              onEditorStateChange={handleEditorStateChange}
              wrapperClassName={styles.editorWrapper}
              editorClassName={styles.editorContent}
              toolbarClassName={styles.editorToolbar}
              toolbar={{
                inline: { inDropdown: true },
                list: { inDropdown: true },
                textAlign: { inDropdown: true },
                link: { inDropdown: true },
                history: { inDropdown: true },
                image: {
                  uploadCallback: (file) =>
                    new Promise((resolve) => {
                      resolve({
                        data: { link: "https://example.com/dummy.png" },
                      });
                    }),
                  alt: { present: true, mandatory: true },
                },
              }}
            /> */}
            <ReactQuill
                          theme="snow"
                          value={adminpagecontent}
                          onChange={handleEditorChange}
                          style={{ height: "250px", marginBottom: "40px" }}
                          modules={{
                            toolbar: [
                              [{ font: [] }],
                              [{ size: ["small", false, "large", "huge"] }],
                              ["bold", "italic", "underline", "strike"],
                              [{ color: [] }, { background: [] }],
                              [{ script: "sub" }, { script: "super" }],
                              [{ header: [1, 2, 3, 4, 5, 6, false] }],
                              [{ align: [] }],
                              [{ list: "ordered" }, { list: "bullet" }],
                              [{ indent: "-1" }, { indent: "+1" }],
                              ["blockquote", "code-block"],
                              ["link", "image", "video"],
                              ["clean"],
                            ],
                          }}
                        />
          </div>
          {formErrors.adminpagecontent && (
            <div className={styles.errorMessage}>
              {formErrors.adminpagecontent}
            </div>
          )}
        </div>
        {errorMessage && (
          <div className={styles.errorMessage}>{errorMessage}</div>
        )}
        {successMessage && (
          <div className={styles.successMessage}>{successMessage}</div>
        )}
        {/* Buttons */}
        <div className={styles.buttonContainer}>
          <button
            type="button"
            onClick={handleSave}
            className={styles.submitButton}
          >
            {loading ? "Saving..." : "Save Changes"}
          </button>
          <button
            type="button"
            onClick={clearForm}
            className={styles.submitButton}
            style={{ marginLeft: "10px", backgroundColor: "red" }}
          >
            Clear
          </button>
        </div>
      </form>
    </div>
  );
};

export default CompanyEditDepartment;
